import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plane, Home, FileCheck, DollarSign, Globe, Phone } from 'lucide-react';

const DiasporaGuide = () => {
    return (
        <div className="space-y-8">
            {/* Hero Section */}
            <div className="bg-blue-600 rounded-xl p-8 text-white shadow-lg relative overflow-hidden">
                <div className="relative z-10">
                    <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                        <Plane className="w-8 h-8" /> Atención a Hermanos Lejanos
                    </h2>
                    <p className="text-blue-100 text-lg max-w-2xl">
                        Sabemos que tu sueño es invertir en tu tierra. Hemos diseñado este espacio exclusivo para salvadoreños en el exterior, con los requisitos y pasos específicos para que compres tu casa en El Salvador desde donde estés.
                    </p>
                </div>
                <Globe className="absolute right-[-20px] bottom-[-40px] w-64 h-64 text-blue-500 opacity-20 rotate-12" />
            </div>

            {/* Main Options Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Option 1: FSV */}
                <Card className="border-t-4 border-t-orange-500 hover:shadow-md transition-shadow">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-orange-600">
                            <Home className="w-5 h-5" /> Fondo Social para la Vivienda (FSV)
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-gray-600">
                            El programa más popular por sus bajas tasas y facilidades. Puedes aplicar si tienes Estatus Migratorio Regular o Irregular (con restricciones).
                        </p>
                        <div className="bg-orange-50 p-4 rounded-lg space-y-2 text-sm">
                            <h4 className="font-bold text-orange-800">Requisitos Clave:</h4>
                            <ul className="list-disc list-inside text-gray-700 space-y-1">
                                <li>Poder (Mandato) a un representante en El Salvador.</li>
                                <li>Comprobantes de ingresos (Income Tax, W-2, Remesas).</li>
                                <li>Récord crediticio en el país de residencia.</li>
                            </ul>
                        </div>
                        <Button asChild className="w-full bg-orange-500 hover:bg-orange-600">
                            <a href="https://www.fsv.gob.sv/hermanos-lejanos/" target="_blank" rel="noreferrer">
                                Ver Programa FSV
                            </a>
                        </Button>
                    </CardContent>
                </Card>

                {/* Option 2: Private Banks */}
                <Card className="border-t-4 border-t-blue-800 hover:shadow-md transition-shadow">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-blue-800">
                            <DollarSign className="w-5 h-5" /> Banca Privada (Agrícola, Cuscatlán, etc.)
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-gray-600">
                            Ideal para propiedades de mayor valor o si buscas agilidad. Generalmente requieren estatus legal (Residencia/Ciudadanía).
                        </p>
                        <div className="bg-blue-50 p-4 rounded-lg space-y-2 text-sm">
                            <h4 className="font-bold text-blue-800">Requisitos Clave:</h4>
                            <ul className="list-disc list-inside text-gray-700 space-y-1">
                                <li>Estatus migratorio legal comprobable.</li>
                                <li>Declaraciones de impuestos de los últimos 2 años.</li>
                                <li>Buró de crédito (Equifax/TransUnion) excelente.</li>
                            </ul>
                        </div>
                        <Button asChild variant="outline" className="w-full border-blue-800 text-blue-800 hover:bg-blue-50">
                            <a href="https://www.bancoagricola.com/salvadorenos-en-el-exterior" target="_blank" rel="noreferrer">
                                Ver Opción Banco Agrícola
                            </a>
                        </Button>
                    </CardContent>
                </Card>
            </div>

            {/* Steps Section */}
            <div className="bg-white p-6 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <FileCheck className="w-6 h-6 text-green-600" /> Pasos para Comprar desde el Extranjero
                </h3>
                <div className="space-y-6">
                    <Step
                        number="1"
                        title="Designar un Representante (Apoderado)"
                        desc="Necesitas a alguien de confianza en El Salvador. Debes enviarle un 'Poder Especial Administrativo' elaborado por un notario salvadoreño o ante el Consulado."
                    />
                    <Step
                        number="2"
                        title="Pre-calificación"
                        desc="Envía tus documentos de ingresos escaneados para saber cuánto te prestan. No busques casa sin saber tu presupuesto real."
                    />
                    <Step
                        number="3"
                        title="Selección de Propiedad"
                        desc="Tu apoderado visita las opciones. Nosotros te enviamos videos, fotos 360° y videollamadas en vivo para que la veas como si estuvieras aquí."
                    />
                    <Step
                        number="4"
                        title="Cierre y Firma"
                        desc="Una vez aprobado el crédito, tu apoderado firma la escritura en tu nombre. ¡La casa es tuya!"
                    />
                </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-gray-900 text-white p-8 rounded-xl text-center">
                <h3 className="text-2xl font-bold mb-2">¿Necesitas ayuda con el trámite?</h3>
                <p className="text-gray-400 mb-6">Tenemos experiencia ayudando a compatriotas a invertir de forma segura.</p>
                <Button className="bg-green-500 hover:bg-green-600 gap-2" size="lg">
                    <Phone className="w-4 h-4" /> Contactar Asesor de Hermanos Lejanos
                </Button>
            </div>
        </div>
    );
};

const Step = ({ number, title, desc }) => (
    <div className="flex gap-4">
        <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 font-bold rounded-full flex items-center justify-center border border-green-200">
            {number}
        </div>
        <div>
            <h4 className="font-bold text-gray-800">{title}</h4>
            <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
        </div>
    </div>
);

export default DiasporaGuide;
